/* eslint-disable max-lines */
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  pinsAtom,
  useRemovePin,
  useSubmitPinMessage,
  type AcknowledgmentPayload,
  type PinMessage,
  type ResultPayload
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinClarificationClicked,
  trackDesignLabPinDeleteFailed,
  trackDesignLabPinDeleted,
  trackDesignLabPinError,
  trackDesignLabPinMessageSubmitted,
  trackDesignLabPinNeedsClarification,
  trackDesignLabPinReady,
  trackDesignLabPinSelected,
  trackDesignLabPinUnsupported
} from "@design-lab/shared/lib/analytics";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useToast } from "@design-lab/shared/ui";
import { useAtom, useAtomValue } from "jotai";
import { Loader2, Pin, Sparkles, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MarkdownContent, processTextWithHexCodes } from "./MarkdownContent";
import { useGoogleFont } from "./useGoogleFont";

function getMessageCounts(
  messages: PinMessage[],
  pendingRole?: "user" | "ai"
): { userMessagesCount: number; aiMessagesCount: number } {
  const userMessagesCount =
    messages.filter(m => m.role === "user").length + (pendingRole === "user" ? 1 : 0);
  const aiMessagesCount =
    messages.filter(m => m.role === "ai").length + (pendingRole === "ai" ? 1 : 0);
  return { userMessagesCount, aiMessagesCount };
}

function FontOptionButton({
  opt,
  onPress,
  isDisabled
}: {
  opt: { label: string; value?: string };
  onPress: () => void;
  isDisabled: boolean;
}) {
  const fontName = opt.value || opt.label;
  useGoogleFont(fontName);

  return (
    <Button
      data-testid={`pin-clarify-${opt.label}`}
      className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
      style={{ fontFamily: `"${fontName}", sans-serif` }}
      onPress={onPress}
      isDisabled={isDisabled}
    >
      {opt.label}
    </Button>
  );
}

// eslint-disable-next-line max-lines-per-function
export function PinningPanel() {
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentSessionId = currentPinningSession?.sessionId;

  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState<Set<string>>(new Set());

  const clearRemovingPin = useCallback((pinId: string) => {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }, []);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { mutate: removePinApi } = useRemovePin();
  const { error: showErrorToast } = useToast();

  const activePin = useMemo(
    () => pins.find(p => p.id === activePinId) ?? null,
    [activePinId, pins]
  );

  // Clear pins and active pin when pinning mode is closed
  useEffect(
    () => () => {
      setPins([]);
      setActivePinId(null);
    },
    [setPins, setActivePinId]
  );

  useEffect(() => {
    if (activePinId) return;
    if (pins.length === 0) return;
    setActivePinId(pins[0]?.id ?? null);
  }, [activePinId, pins, setActivePinId]);

  useEffect(() => {
    setComposerText("");
  }, [activePinId]);

  // Auto-focus and scroll to composer when it becomes available
  useEffect(() => {
    // Focus when:
    // 1. New pin (draft with no messages)
    // 2. Follow-up question needed (needs_clarification state)
    // 3. Pin returns to draft state after processing
    const shouldFocus =
      (activePin?.state === "draft" && activePin.messages.length === 0) ||
      activePin?.state === "needs_clarification";

    if (shouldFocus && textareaRef.current) {
      // Small timeout to ensure DOM is ready and animation is complete
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }, 100);
    }
  }, [activePin?.state, activePin?.messages.length, activePinId]);

  const createUuid = useCallback(() => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
  }, []);

  const updatePin = useCallback(
    (
      pinId: string,
      updater: (pin: { id: string; state: any; messages: PinMessage[]; coordinates: any }) => any
    ) => {
      setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin as any) : pin)));
    },
    [setPins]
  );

  const deletePin = useCallback(
    (pinId: string) => {
      setPins(prev => prev.filter(p => p.id !== pinId));
      if (activePinId === pinId) {
        setActivePinId(null);
      }
    },
    [activePinId, setActivePinId, setPins]
  );

  const handleDeletePin = useCallback(
    (pinId: string) => {
      const sessionId = currentSessionId;

      // Fallback to local-only delete if no session
      if (!sessionId) {
        deletePin(pinId);
        return;
      }

      // Check if pin exists in backend:
      // - Has any messages (user messages from sending, or AI messages from server hydration)
      // - Or has a non-draft state (was processed by server)
      const pin = pins.find(p => p.id === pinId);
      const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

      // If pin hasn't been synced to backend yet, just delete locally
      if (!hasBeenSyncedToBackend) {
        deletePin(pinId);
        return;
      }

      // Prevent duplicate delete requests for the same pin
      if (removingPinIds.has(pinId)) {
        return;
      }

      setRemovingPinIds(prev => new Set(prev).add(pinId));

      removePinApi(
        { sessionId, pinId },
        {
          onSuccess: () => {
            // Track successful pin deletion
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleted({
                artworkId: currentArtworkId!,
                sessionId: currentSessionId!,
                pinId,
                pinState: pin?.state ?? "draft",
                hadMessages: (pin?.messages?.length ?? 0) > 0
              });
            }
            deletePin(pinId);
            clearRemovingPin(pinId);
          },
          onError: () => {
            // Track failed pin deletion
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleteFailed({
                artworkId: currentArtworkId!,
                sessionId: currentSessionId!,
                pinId
              });
            }
            clearRemovingPin(pinId);
            showErrorToast({
              title: "Failed to remove pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [
      currentArtworkId,
      currentSessionId,
      removePinApi,
      deletePin,
      pins,
      removingPinIds,
      clearRemovingPin,
      showErrorToast
    ]
  );

  const { mutate: submitMessage } = useSubmitPinMessage({
    onAcknowledgment: (payload: AcknowledgmentPayload) => {
      if (!activePinId) return;
      updatePin(activePinId, pin => ({
        ...pin,
        thinkingText: payload.message || undefined
      }));
    },
    onResult: (payload: ResultPayload) => {
      if (!activePinId) return;

      const nextState =
        payload.status === "ready" ||
        payload.status === "needs_clarification" ||
        payload.status === "unsupported"
          ? payload.status
          : "ready";

      const options = payload.clarification?.options ?? [];
      const questionType = payload.clarification?.questionType;
      const resultMessage = payload.message || "Done.";

      // Get current pin to calculate message counts (AI message will be added)
      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      const isClarifying = nextState === "needs_clarification" && options.length > 0;

      updatePin(activePinId, pin => ({
        ...pin,
        state: nextState,
        instruction: payload.instruction,
        thinkingText: undefined, // Clear thinking state
        messages: [
          ...pin.messages,
          {
            id: createUuid(),
            role: "ai",
            content: resultMessage,
            timestamp: Date.now(),
            ...(isClarifying && { options, questionType })
          }
        ]
      }));

      // Track pin state changes
      if (currentArtworkId && currentSessionId) {
        const trackingData = {
          artworkId: currentArtworkId!,
          sessionId: currentSessionId!,
          pinId: activePinId,
          userMessagesCount,
          aiMessagesCount
        };

        if (nextState === "ready") {
          trackDesignLabPinReady(trackingData);
        } else if (nextState === "unsupported") {
          trackDesignLabPinUnsupported(trackingData);
        } else if (nextState === "needs_clarification") {
          trackDesignLabPinNeedsClarification({
            ...trackingData,
            optionsCount: options.length,
            questionType
          });
        }
      }
    },
    onError: () => {
      if (!activePinId) return;

      // Get current pin to calculate message counts (AI error message will be added)
      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      // Track pin error
      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinError({
          artworkId: currentArtworkId!,
          sessionId: currentSessionId!,
          pinId: activePinId,
          errorType: "processing_error",
          userMessagesCount,
          aiMessagesCount
        });
      }

      updatePin(activePinId, pin => ({
        ...pin,
        state: "draft",
        messages: [
          ...pin.messages,
          {
            id: createUuid(),
            role: "ai",
            content: "Something went wrong. Please try again.",
            timestamp: Date.now()
          }
        ]
      }));
    }
  });

  const handleSubmit = useCallback(
    (text: string, isClarification?: boolean, clarificationValue?: string) => {
      if (!activePin) return;
      const sessionId = currentSessionId ?? null;

      const trimmed = text.trim();
      if (!trimmed) return;
      if (activePin.state === "processing") return;

      const pinId = activePin.id;
      const now = Date.now();
      const userMessageId = createUuid();
      const isFirstMessage = activePin.messages.filter(m => m.role === "user").length === 0;
      const turnIndex = activePin.messages.filter(m => m.role === "user").length + 1;

      // Optimistic user message + processing state
      updatePin(pinId, pin => ({
        ...pin,
        state: "processing" as const,
        thinkingText: undefined, // Will show default "Thinking..."
        messages: [
          ...pin.messages.map(m => ({ ...m, options: undefined })), // clear prior clarification pills
          { id: userMessageId, role: "user" as const, content: trimmed, timestamp: now }
        ]
      }));
      setComposerText("");

      // Track message submission or clarification (user message being added)
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(activePin.messages, "user");

      if (currentArtworkId && currentSessionId) {
        if (isClarification && clarificationValue) {
          trackDesignLabPinClarificationClicked({
            artworkId: currentArtworkId!,
            sessionId: currentSessionId!,
            pinId,
            optionValue: clarificationValue,
            turnIndex,
            userMessagesCount,
            aiMessagesCount
          });
        } else {
          trackDesignLabPinMessageSubmitted({
            artworkId: currentArtworkId!,
            sessionId: currentSessionId!,
            pinId,
            isFirstMessage,
            turnIndex,
            userMessagesCount,
            aiMessagesCount
          });
        }
      }

      if (!sessionId) {
        return;
      }

      const priorPins = pins
        .filter(p => p.id !== pinId)
        .map(p => ({
          pin_id: p.id,
          coordinates: p.coordinates,
          state: p.state,
          instruction: p.instruction ?? ""
        }));

      submitMessage({
        sessionId,
        pinId,
        coordinates: activePin.coordinates,
        userMessage: trimmed,
        isFirstMessage,
        priorPins
      });
    },
    [activePin, createUuid, currentArtworkId, currentSessionId, pins, submitMessage, updatePin]
  );

  const lastAiMessageWithOptions = useMemo(() => {
    if (!activePin) return null;
    for (let i = activePin.messages.length - 1; i >= 0; i -= 1) {
      const msg = activePin.messages[i];
      if (msg?.role === "ai" && msg.options && msg.options.length > 0) return msg;
    }
    return null;
  }, [activePin]);

  return (
    <Card className="flex h-full w-full max-w-[700px] flex-col border-none shadow-none lg:border lg:shadow">
      <CardContent className="flex flex-1 flex-col overflow-hidden p-0">
        <div className="flex flex-1 flex-col overflow-y-auto p-4">
          {pins.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-gray-500">
              <Pin className="mb-4 h-16 w-16 -rotate-45 text-gray-300" />
              <p className="max-w-[280px] text-sm leading-relaxed">
                No instructions added yet. Please click anywhere on the image to drop a pin and add
                instructions for our designers.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 pb-4">
              {pins.map((pin, index) => {
                const isActive = pin.id === activePinId;
                const isProcessing = pin.state === "processing";
                return (
                  <div
                    key={pin.id}
                    className={`flex flex-col gap-3 rounded-lg border p-4 transition-colors ${
                      isActive ? "border-blue-200 bg-blue-50/30" : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="flex cursor-pointer items-center gap-2 font-medium"
                        onClick={() => {
                          const previousPinId = activePinId;
                          const newPinId = isActive ? null : pin.id;
                          setActivePinId(newPinId);

                          // Track pin selection (only when selecting a pin, not deselecting)
                          if (newPinId && currentArtworkId && currentSessionId) {
                            trackDesignLabPinSelected({
                              artworkId: currentArtworkId!,
                              sessionId: currentSessionId!,
                              pinId: newPinId,
                              previousPinId,
                              pinState: pin.state
                            });
                          }
                        }}
                      >
                        <span className="text-sm font-semibold text-gray-900">
                          Pin {index + 1}:{" "}
                          <span className="font-normal text-gray-500">
                            What would you like to do?
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`pin-delete-${pin.id}`}
                          aria-label={`Delete pin ${pin.id}`}
                          onPress={() => handleDeletePin(pin.id)}
                          variant="icon"
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                          isDisabled={removingPinIds.has(pin.id)}
                        >
                          {removingPinIds.has(pin.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Content area: either messages list or final instruction */}
                    {(pin.messages.length > 0 || (pin.state === "ready" && pin.instruction)) && (
                      <div className="flex flex-col gap-4">
                        {pin.state === "ready" && pin.instruction ? (
                          <div className="rounded-lg border border-[#A5B4FC] bg-white p-4 shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)]">
                            <div className="flex items-start gap-3">
                              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#6366F1]" />
                              <div className="flex flex-col gap-1">
                                <h4 className="text-xs font-bold uppercase tracking-wide text-[#6366F1]">
                                  WE’VE GOT YOUR BACK — OUR DESIGNERS WILL UPDATE THE DESIGN BASED
                                  ON YOUR INSTRUCTIONS BELOW
                                </h4>
                                <p className="text-sm text-gray-700">
                                  {processTextWithHexCodes(pin.instruction || "")}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-4 pl-1">
                            {pin.messages.map(msg => (
                              <div key={msg.id} className="flex flex-col gap-1 text-sm">
                                <span
                                  className={`text-[10px] font-bold uppercase ${msg.role === "user" ? "text-gray-400" : "text-blue-700"}`}
                                >
                                  {msg.role === "user" ? "ME" : "REDRAW ASSISTANT"}
                                </span>
                                {msg.role === "ai" ? (
                                  <MarkdownContent content={msg.content} />
                                ) : (
                                  <div className="whitespace-pre-wrap text-gray-800">
                                    {processTextWithHexCodes(msg.content)}
                                  </div>
                                )}
                              </div>
                            ))}
                            {pin.state === "processing" && (
                              <div className="flex flex-col gap-1 text-sm">
                                <span className="text-[10px] font-bold uppercase text-blue-700">
                                  REDRAW ASSISTANT
                                </span>
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  <span className="text-sm text-gray-500">
                                    {pin.thinkingText || "Thinking..."}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {isActive && pin.state !== "ready" && (
                      <div
                        className={
                          pin.messages.length > 0 ? "mt-2 border-t border-gray-100 pt-3" : ""
                        }
                      >
                        {pin.state === "needs_clarification" &&
                        lastAiMessageWithOptions?.options?.length ? (
                          <div className="mb-3">
                            <div className="mb-2 text-xs font-semibold text-gray-600">Clarify:</div>
                            <div className="flex flex-wrap gap-2">
                              {lastAiMessageWithOptions.options.map(opt => {
                                const isFont =
                                  lastAiMessageWithOptions.questionType === "font_selection";
                                const isColorShade =
                                  lastAiMessageWithOptions.questionType === "color_shade";

                                if (isFont) {
                                  return (
                                    <FontOptionButton
                                      key={`${opt.label}-${opt.value}`}
                                      opt={opt}
                                      onPress={() =>
                                        handleSubmit(
                                          opt.value || opt.label,
                                          true,
                                          opt.value || opt.label
                                        )
                                      }
                                      isDisabled={isProcessing}
                                    />
                                  );
                                }

                                const displayText =
                                  isColorShade && opt.value
                                    ? `${opt.label} (${opt.value})`
                                    : opt.label;

                                return (
                                  <Button
                                    key={`${opt.label}-${opt.value}`}
                                    data-testid={`pin-clarify-${opt.label}`}
                                    className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                    onPress={() =>
                                      handleSubmit(
                                        opt.value || opt.label,
                                        true,
                                        opt.value || opt.label
                                      )
                                    }
                                    isDisabled={isProcessing}
                                  >
                                    {processTextWithHexCodes(displayText)}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}

                        <div className="flex gap-2">
                          <textarea
                            ref={textareaRef}
                            value={composerText}
                            onChange={e => setComposerText(e.target.value)}
                            placeholder="Add your instructions here..."
                            className="min-h-[44px] flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                            disabled={isProcessing}
                            data-testid="pinning-composer"
                          />
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <Button
                            data-testid="pin-composer-cancel"
                            variant="link"
                            className="text-xs font-medium text-gray-500 no-underline hover:text-gray-700 hover:no-underline"
                            onPress={() => handleDeletePin(pin.id)}
                            isDisabled={removingPinIds.has(pin.id)}
                          >
                            {removingPinIds.has(pin.id) ? "Removing..." : "Cancel"}
                          </Button>
                          <Button
                            data-testid="pinning-send"
                            className="rounded bg-blue-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                            isDisabled={isProcessing || !composerText.trim()}
                            onPress={() => handleSubmit(composerText)}
                          >
                            Submit
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
